<script>
import { mapState, mapActions, mapGetters } from "vuex";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { formatHashrate, formatRelativeDate, getBreakpoint } from "@/utils";
import ContractNavigationWrapper from "@/components/ContractNavigationWrapper";

export default {
  // Included components
  components: {
    FontAwesomeIcon,
    ContractNavigationWrapper,
  },

  data() {
    return {
      // Number of contracts to show on each page / current request limit
      PAGE_SIZE: 20,

      // Current request limit
      limit: 20,

      // current request offset
      offset: 0,

      // The current page number (1 indexed)
      currentPage: null,

      // current contracts accessible on the page (currently or through pagination)
      currentContracts: [],

      // Whether there is another page of data
      hasNextPage: false,

      // total number of pages
      totalPages: 0,

      // Whether list view or card view is active
      listView: true,

      // search input
      searchQuery: "",

      // whether data is currently being fetched
      loadingState: true,
    };
  },

  computed: {
    // The current page of contract
    page() {
      return this.currentContracts.slice(0, this.PAGE_SIZE);
    },

    // Mapped vuex state
    ...mapGetters(["can"]),
    ...mapState(["user"]),
  },

  watch: {
    async listView(value) {
      // Display 25 miners per page if list view is active
      this.PAGE_SIZE = value ? 20 : 10;

      // recalculate pagination parameters
      this.totalPages = (this.totalPages * this.limit) / this.PAGE_SIZE; // total contract / new page size
      this.limit = value ? 20 : 10; // fetch only 10 contract for card view per page

      // recalculate current page parameters
      let page_num =
        Math.floor((this.offset + this.limit) / this.PAGE_SIZE) === 0
          ? 1
          : Math.floor((this.offset + this.limit) / this.PAGE_SIZE);
      if (page_num !== parseInt(this.currentPage)) {
        // only change route if page differs after switching
        this.currentPage = page_num;
        this.$router.replace({ path: "/contracts", query: { page: page_num } });
      }

      if (value) {
        if (this.offset % 20 > 0) {
          this.offset = this.offset - 10;
        }
        // switched from card view to list view, refetch to get all 20 contract instead of just 10
        await this.fetchContracts();
      } else {
        // when switching from list to card view, the last 10 contract in the list will no longer be visible, need to change offset
        this.offset = this.offset === 0 ? 0 : this.offset - 10;
      }
    },

    async currentPage(value) {
      // update the offset to reflect the current page and refetch
      // subtract 1 from value to get correct 0 index offset (we want no offset for page 1 and so on)
      let page_num = value === 0 ? 0 : value - 1;
      this.offset = this.limit * page_num;
      await this.fetchContracts();
    },

    searchQuery(value) {
      // if additional input is being provided, reset offset
      if (value) {
        this.offset = 0;
      } else {
        this.offset = this.limit * (this.currentPage - 1);
      }
    },
  },

  async created() {
    if (!this.user.isStaffAdmin) {
      await this.$router.push("/");
    }

    this.currentPage = this.$route.query.page || 1;
    await this.fetchContracts();
  },

  methods: {
    async fetchContracts() {
      this.loadingState = true;
      const inputQuery = this.searchQuery.toLowerCase();
      let result = null;

      if (inputQuery) {
        result = await this.getContracts({
          limit: this.limit,
          offset: this.offset,
          searchString: inputQuery,
        });
      } else {
        result = await this.getContracts({
          limit: this.limit,
          offset: this.offset,
        });
      }

      this.currentContracts = result.contracts;
      this.hasNextPage = result.paging.next_page !== null;
      this.totalPages = result.paging.page_count;
      this.loadingState = false;
    },

    // Helper methods and vuex actions
    formatRelativeDate,
    formatHashrate,
    getBreakpoint,
    ...mapActions(["getContracts"]),
  },
};
</script>
<template>
  <ContractNavigationWrapper>
    <template #breadcrumbs>
      <b-breadcrumb-item to="/contracts" active>Contracts</b-breadcrumb-item>
    </template>
    <b-row class="Toolbar mb-3">
      <b-col>
        <h1>Contracts</h1>
      </b-col>
      <b-col
        class="text-right"
        align-self="center"
        v-if="
          can.workers.edit &&
          !this.loadingState &&
          this.currentContracts.length !== 0
        "
      >
        <span
          id="disabled-wrapper"
          class="d-inline-block float-right"
          tabindex="0"
        >
          <b-button
            v-if="can.groups.edit"
            :class="getBreakpoint().xs ? 'mt-2 ml-1' : 'ml-2'"
            variant="secondary"
            to="/contracts/create"
            :size="getBreakpoint().xs ? 'sm' : 'md'"
          >
            {{ getBreakpoint().xs ? "Add" : "Add Existing Contract" }}
          </b-button>
          <b-button
            v-if="can.groups.edit"
            :class="getBreakpoint().xs ? 'mt-2 ml-1' : 'ml-2'"
            variant="primary"
            to="/contracts/onboard-new-contract"
            :size="getBreakpoint().xs ? 'sm' : 'md'"
          >
            {{ getBreakpoint().xs ? "Onboard Client" : "Onboard New Client" }}
          </b-button>
        </span>
      </b-col>
    </b-row>

    <b-row align-v="center">
      <b-col :cols="getBreakpoint().xs ? '9' : '12'" class="mb-3">
        <!-- <b-input-group>
          <b-form-input
            placeholder="Search for a contract"
            v-model="searchQuery"
            v-debounce.fireonempty="fetchContracts"
          ></b-form-input>
          <b-input-group-append>
            <b-button id="search-button">
              <FontAwesomeIcon icon="search" />
            </b-button>
          </b-input-group-append>
        </b-input-group> -->
      </b-col>
      <b-col
        cols="3"
        align-self="center"
        class="mb-3"
        v-if="getBreakpoint().xs"
      >
        <b-button
          variant="light"
          :pressed.sync="listView"
          v-if="getBreakpoint().xs"
        >
          <FontAwesomeIcon icon="list" size="lg" />
        </b-button>
      </b-col>
    </b-row>

    <b-row v-if="loadingState">
      <b-col class="text-center mt-5">
        <div class="mb-4">
          <b-spinner
            style="width: 4rem; height: 4rem"
            variant="primary"
            label="Loading Contracts..."
          ></b-spinner>
        </div>
        <b>Loading Contracts...</b>
      </b-col>
    </b-row>

    <b-row v-else-if="this.currentContracts.length === 0">
      <b-col>
        <b-card class="text-center py-5">
          <b-card-title>No Contracts</b-card-title>
          <b-card-text>Onboard new client or Add existing contract</b-card-text>
          <span>
            <b-button
              v-if="can.groups.edit"
              :class="getBreakpoint().xs ? 'mt-2 ml-1' : 'ml-2'"
              variant="primary"
              to="/contracts/onboard-new-contract"
              :size="getBreakpoint().xs ? 'sm' : 'md'"
            >
              {{ getBreakpoint().xs ? "Onboard Client" : "Onboard New Client" }}
            </b-button>
            <b-button
              v-if="can.groups.edit"
              :class="getBreakpoint().xs ? 'mt-2 ml-1' : 'ml-2'"
              variant="secondary"
              to="/contracts/create"
              :size="getBreakpoint().xs ? 'sm' : 'md'"
            >
              {{ getBreakpoint().xs ? "Add" : "Add Existing Contract" }}
            </b-button>
          </span>
        </b-card>
      </b-col>
    </b-row>

    <b-row v-else-if="listView && !loadingState">
      <b-col>
        <b-table-simple responsive striped hover>
          <b-thead>
            <b-tr>
              <b-th>Contract</b-th>
              <b-th>Device</b-th>
              <b-th>Machine Count</b-th>
              <b-th>Rate/kWh</b-th>
              <b-th>Expiration</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="(contract, index) in page" :key="index">
              <b-td>
                <b-link :to="`/contracts/${contract.id}`">
                  {{
                    contract.team.handle +
                    "." +
                    contract.facility.name +
                    "." +
                    contract.batch_number
                  }}
                </b-link>
              </b-td>
              <b-td>{{ contract.device_type.name }}</b-td>
              <b-td>{{ contract.machine_count }}</b-td>
              <b-td>
                {{ contract.rate_per_kWh + " kWh" }}
              </b-td>
              <b-td>
                {{ contract.expiration }}
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </b-col>
    </b-row>

    <b-pagination-nav
      v-if="this.totalPages > 1 && !loadingState"
      :number-of-pages="this.totalPages"
      base-url="?page="
      align="center"
      limit="7"
      size="sm"
      v-model="currentPage"
      :use-router="true"
      :hide-goto-end-buttons="this.totalPages < 3"
    ></b-pagination-nav>
  </ContractNavigationWrapper>
</template>

<style scoped>
.Worker-card {
  margin-bottom: 2rem;
}

.Worker-card-title {
  display: flex;
  align-items: center;
}

.Worker-card .Worker-card-link-indicator {
  display: inline-flex;
  align-items: center;

  opacity: 0;

  transition: all 200ms;
}

.Worker-card:hover .Worker-card-link-indicator {
  opacity: 1;
  margin-left: 0.35rem;
}

.Worker-card a {
  color: unset;
  font-weight: unset;
  text-decoration: unset;
}

.Worker-card:hover .Worker-card-title {
  color: #007bff;
}

#listview {
  display: inline-block;
  margin-top: 7px;
}

#search-button {
  border: 1px solid transparent;
  box-shadow: 0 0 2px 0 rgba(0, 20, 40, 0.4);
}

#list-more:hover {
  cursor: pointer;
}
</style>
